import type { EntityData } from "@/types/entity";
import type {
  DecizieInregistrare,
  DecizieInregistrareInput,
} from "@/types/decizieInregistrare";
import type { NavigationPage, PageStatus } from "@/types/navigation";
import type { RiskAnalysisBySession } from "@/types/risk-analysis";
import type {
  DocumentChecklistItem,
  GuideStepStatus,
} from "@/types/guide-step";
import type { CardStatus } from "@/components/ui/status-badge";
import type { MaturityEvaluation } from "@/types/maturity";
import {
  isContactDataComplete,
  isConfirmationsComplete,
} from "@/utils/contactValidation";
import { validateDecizieFields } from "@/utils/decizieInregistrareUtils";
import { isCompanySectionComplete } from "@/utils/onboardingValidation";
import { isRelatieStatComplete } from "@/utils/relatieStatValidation";
import { validateSituatieServiciiUE } from "@/utils/serviciiUeValidation";

export function mapGuideStatusToCardStatus(
  guideStatus: GuideStepStatus,
  completedLabel: "finalizat" | "transmis" | "primita" = "finalizat"
): CardStatus {
  switch (guideStatus) {
    case "completed":
      return completedLabel;
    case "in_progress":
      return "in-lucru";
    case "not_started":
      return "neinceput";
    case "not_applicable":
      return "neinceput";
  }
}

export function calculateStepProgress(status: GuideStepStatus): number {
  switch (status) {
    case "not_started":
      return 0;
    case "in_progress":
      return 50;
    case "completed":
      return 100;
    case "not_applicable":
      return 0;
  }
}

export function getIdentificareStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  if (!entityData) return "not_started";

  const classification = entityData.nis_classification?.classificationV2;
  const hasContactData = !!entityData.contact_data;

  if (classification && classification !== "IMPACT" && hasContactData) {
    return "completed";
  }

  return "in_progress";
}

export function getEfectPerturbatorStatus(
  entityData: EntityData | null | undefined
): {
  status: GuideStepStatus;
  isVisible: boolean;
} {
  if (!entityData) {
    return { status: "not_applicable", isVisible: false };
  }

  const classification = entityData.nis_classification?.classificationV2;
  const hasCompletedResult = !!entityData.impact_analysis?.result;

  if (classification !== "IMPACT" && !hasCompletedResult) {
    return { status: "not_applicable", isVisible: false };
  }

  if (hasCompletedResult) {
    return { status: "completed", isVisible: true };
  }

  if (entityData.impact_analysis) {
    return { status: "in_progress", isVisible: true };
  }

  return { status: "not_started", isVisible: true };
}

export function getLockedTooltip(pageId: string): string | null {
  switch (pageId) {
    case "analiza-risc":
      return "Finalizați Formularul de Notificare pentru a accesa Analiza de risc";
    case "evaluare-maturitate":
      return "Finalizați Analiza de risc pentru a accesa Evaluarea de maturitate";
    case "onb-decizie-inregistrare":
    case "onb-evaluare-nivel-risc":
    case "onb-decizie-analiza-risc":
    case "onb-evaluare-maturitate":
    case "onb-decizie-evaluare-maturitate":
      return "În curând";
    default:
      return null;
  }
}

export function getAnalizaRiscStatus(
  riskData: RiskAnalysisBySession | undefined
): GuideStepStatus {
  if (!riskData?.risk_analyses?.length) {
    return "not_started";
  }

  const allSectorsComplete = riskData.risk_analyses.every(
    (ra) => ra.has_populated_matrix === true
  );

  if (allSectorsComplete) {
    return "completed";
  }

  return "in_progress";
}

export type MaturityStatus = "finalizat" | "in-lucru" | "neinceput" | "blocat";

export function getMaturityStatus(
  maturityEval: MaturityEvaluation | null | undefined,
  isRiskCompleted: boolean
): MaturityStatus {
  if (!isRiskCompleted) return "blocat";
  if (!maturityEval) return "neinceput";
  if (maturityEval.eval_state === "FULL_COMPLETE") return "finalizat";
  if (maturityEval.eval_state === "PRELIM_COMPLETE") return "in-lucru";
  const responses = maturityEval.preliminary_responses;
  if (responses && Object.values(responses).some(Boolean)) return "in-lucru";
  return "neinceput";
}

export function calculateEfectPerturbatorProgress(
  entityData: EntityData | null | undefined
): number {
  if (!entityData?.impact_analysis) return 0;
  return 100;
}

export function calculateAnalizaRiscProgress(
  riskData: RiskAnalysisBySession | undefined
): number {
  if (!riskData || riskData.total_sectors === 0) return 0;
  return Math.round(
    (riskData.completed_sectors / riskData.total_sectors) * 100
  );
}

export function getDocumentChecklistStatus(
  entityData: EntityData | null | undefined
): DocumentChecklistItem[] {
  return [
    {
      text: "Certificat de Înregistrare (CUI)",
      status: entityData?.cui ? "provided" : "missing",
      required: true,
    },
    {
      text: "Bilanț contabil (ultimul an fiscal)",
      status: entityData?.infocui_raw_data ? "provided" : "missing",
      required: true,
    },
    {
      text: "Număr mediu salariați",
      status: entityData?.nr_salariati ? "provided" : "missing",
      required: true,
    },
    {
      text: "Cifra de afaceri / Total active",
      status:
        entityData?.cifra_afaceri && entityData?.active
          ? "provided"
          : "missing",
      required: true,
    },
    {
      text: "Lista codurilor CAEN",
      status: entityData?.caen_principal?.cod ? "provided" : "missing",
      required: true,
    },
    {
      text: "Certificări securitate (ISO 27001)",
      status: "optional",
      required: false,
    },
  ];
}

export function getNotificationCuiStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  if (!entityData) return "not_started";

  if (entityData.cui && entityData.anaf_raw_data) {
    return "completed";
  }

  if (entityData.cui) {
    return "in_progress";
  }

  return "not_started";
}

export function getNotificationDimensiuneStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  if (!entityData) return "not_started";

  if (entityData.dimensiune && entityData.nr_salariati) {
    return "completed";
  }

  if (entityData.cifra_afaceri || entityData.nr_salariati) {
    return "in_progress";
  }

  return "not_started";
}

export function getNotificationServiciiStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  if (!entityData) return "not_started";

  if (entityData.selected_services && entityData.selected_services.length > 0) {
    return "completed";
  }

  if (entityData.dimensiune) {
    return "in_progress";
  }

  return "not_started";
}

export function getNotificationClasificareStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  if (!entityData) return "not_started";

  if (entityData.nis_classification) {
    return "completed";
  }

  if (entityData.selected_services && entityData.selected_services.length > 0) {
    return "in_progress";
  }

  return "not_started";
}

export type NotificationPdfChecks = Record<string, boolean>;

export function getNotificationPdfChecks(
  entityData: EntityData | null | undefined
): NotificationPdfChecks {
  if (!entityData) {
    return {
      company: false,
      financial: false,
      services: false,
      classification: false,
      contacts: false,
      confirmations: false,
      documente_anexate: false,
      relatie_stat: false,
      servicii_ue: false,
    };
  }

  const doc = entityData.documente_anexate;
  const hasDocumenteAnexate =
    doc?.avem_documente != null &&
    (doc.avem_documente !== true ||
      Boolean(doc.nr_documente?.trim() && doc.doc_dimensiune?.trim()));

  return {
    company: isCompanySectionComplete(entityData),
    financial: Boolean(
      entityData.nr_salariati && entityData.cifra_afaceri && entityData.active
    ),
    services: (entityData.selected_services?.length ?? 0) > 0,
    classification: Boolean(entityData.nis_classification?.classificationV2),
    contacts: entityData.contact_data
      ? isContactDataComplete(entityData.contact_data)
      : false,
    confirmations: entityData.contact_data
      ? isConfirmationsComplete(entityData.contact_data)
      : false,
    documente_anexate: hasDocumenteAnexate,
    relatie_stat: isRelatieStatComplete(entityData.relatie_stat_roman),
    servicii_ue:
      Object.keys(validateSituatieServiciiUE(entityData.situatie_servicii_ue))
        .length === 0,
  };
}

export function getNotificationPdfStatusFromChecks(
  checks: NotificationPdfChecks
): GuideStepStatus {
  const allComplete = Object.values(checks).every(Boolean);
  if (allComplete) return "completed";

  const anyStarted = Object.values(checks).some(Boolean);
  if (anyStarted) return "in_progress";

  return "not_started";
}

export function getNotificationPdfProgressFromChecks(
  checks: NotificationPdfChecks
): number {
  const total = Object.keys(checks).length;
  if (total === 0) return 0;
  const completed = Object.values(checks).filter(Boolean).length;
  return Math.round((completed / total) * 100);
}

export function getNotificationPdfStatus(
  entityData: EntityData | null | undefined
): GuideStepStatus {
  const checks = getNotificationPdfChecks(entityData);
  return getNotificationPdfStatusFromChecks(checks);
}

export function isRiskAnalysisUnlocked(
  entityData: EntityData | null | undefined
): boolean {
  return getNotificationPdfStatus(entityData) === "completed";
}

export function getDecizieInregistrareStatus(
  decizieData: DecizieInregistrare | null | undefined
): GuideStepStatus {
  if (!decizieData) return "not_started";
  const { isValid } = validateDecizieFields(
    decizieData as DecizieInregistrareInput
  );
  return isValid ? "completed" : "in_progress";
}

// Resolves the display status for a navigation page based on entity/risk data.
//
//   resolvePageStatus(page, entityData, riskData, decizieData?)
//     │
//     ├─ "formular-notificare"      → getNotificationPdfStatus(entityData)
//     ├─ "notificare-entitate"      → getNotificationPdfStatus(entityData)
//     ├─ "decizie-inregistrare"     → locked if notification incomplete,
//     │                                else getDecizieInregistrareStatus(decizieData)
//     ├─ "analiza-risc"             → locked if identification incomplete, else computed
//     ├─ "evaluare-maturitate"      → locked if analiza-risc incomplete, else "not_started"
//     ├─ "analiza-efect-perturbator"→ getEfectPerturbatorStatus(entityData).status
//     └─ default                    → page.status
//
export function resolvePageStatus(
  page: NavigationPage,
  entityData: EntityData | null | undefined,
  riskData: RiskAnalysisBySession | undefined,
  decizieData?: DecizieInregistrare | null
): PageStatus | GuideStepStatus | undefined {
  switch (page.id) {
    case "formular-notificare":
    case "notificare-entitate":
      return getNotificationPdfStatus(entityData);
    case "analiza-risc":
      if (!isRiskAnalysisUnlocked(entityData)) return "locked";
      return getAnalizaRiscStatus(riskData);
    case "evaluare-maturitate":
      if (getAnalizaRiscStatus(riskData) !== "completed") return "locked";
      return "not_started";
    case "analiza-efect-perturbator":
      return getEfectPerturbatorStatus(entityData).status;
    case "decizie-inregistrare":
      if (getNotificationPdfStatus(entityData) !== "completed") return "locked";
      return getDecizieInregistrareStatus(decizieData);
    default:
      return page.status;
  }
}
